import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';

import {
  GET_ALL_FILTER_TYPES_QUERY,
  GET_MY_FIND_QUERY,
  GET_MY_FIND_GROUPS_QUERY,
  GET_MY_SITES_QUERY,
} from '../../gql';
import { FindSkeleton } from './FindSkeleton';
import { FindNotFound } from './FindNotFound';
import { FindForm } from './FindForm';
import { FindData } from '../../pages/Find/types';
import { useVocabulary } from '../../i18n';

interface FindSettingsProps extends FindData {
  onSaveDisabledChange?: (disabled: boolean) => void;
}

const FindSettings = React.memo(
  ({
    called,
    loading,
    find,
    findId,
    forSite,
    onSaveDisabledChange,
  }: FindSettingsProps) => {
    const vocabulary = useVocabulary();
    const [searchParams] = useSearchParams();
    const cloneFindId = !findId
      ? searchParams.get('clone') || undefined
      : undefined;
    const {
      called: cloneCalled,
      data: cloneData,
      loading: cloneLoading,
    } = useQuery(GET_MY_FIND_QUERY, {
      variables: { findId: cloneFindId },
      fetchPolicy: 'cache-first',
      skip: !cloneFindId,
    });
    const {
      data: availableFiltersData,
      loading: availableFiltersLoading,
    } = useQuery(GET_ALL_FILTER_TYPES_QUERY, {
      fetchPolicy: 'cache-first',
    });

    const { data: sitesData, loading: sitesLoading } = useQuery(
      GET_MY_SITES_QUERY,
      {
        fetchPolicy: 'cache-first',
      }
    );
    const {
      data: groupsData,
      loading: groupsLoading,
    } = useQuery(GET_MY_FIND_GROUPS_QUERY, { fetchPolicy: 'cache-first' });

    const sites = sitesData?.getMySites || [];

    const materials = useMemo(
      () =>
        (availableFiltersData?.getAllFilterTypes?.materials || []).toSorted(
          (a, b) => a - b
        ),
      [availableFiltersData?.getAllFilterTypes?.materials]
    );

    const types = useMemo<Array<{ id: string; category: string }>>(
      () =>
        (availableFiltersData?.getAllFilterTypes?.findTypes || []).toSorted(
          (a, b) => a - b
        ),
      [availableFiltersData?.getAllFilterTypes?.findTypes]
    );

    const eras = useMemo<
      Array<{
        id: string;
        label: string;
        parentId: string;
        parentLabel: string;
      }>
    >(
      () =>
        (availableFiltersData?.getAllFilterTypes?.eras || [])
          .filter((e) => e.parentId)
          // Adding Indeterminate dating because originally we have it only as a parent item that gets filtered out
          .concat([
            {
              id: 'indeterminate dating',
              label: vocabulary('era', 'indeterminate dating'),
              parentId: 'indeterminate dating',
              ordinal: 26,
            },
          ])
          .map((era) => ({
            ...era,
            id: era.id.toLocaleLowerCase(),
            label: vocabulary('era', era.id),
            parentId: era.parentId.toLocaleLowerCase(),
            parentLabel: vocabulary('era', era.parentId),
          }))
          .toSorted((a, b) => a - b),
      [availableFiltersData?.getAllFilterTypes?.eras, vocabulary]
    );

    const tags = useMemo<Array<string>>(
      () =>
        (availableFiltersData?.getAllFilterTypes?.tags || []).map(
          (t) => t.name
        ),
      [availableFiltersData?.getAllFilterTypes?.tags]
    );

    const groups = useMemo(() => groupsData?.getMyFindGroups || [], [
      groupsData?.getMyFindGroups,
    ]);
    const formFind = cloneFindId
      ? cloneData?.getMyFind
      : findId
      ? find
      : undefined;
    const findCalled = cloneFindId ? cloneCalled : called;
    const findLoading = cloneFindId ? cloneLoading : loading;
    const formKey = cloneFindId
      ? `clone:${cloneFindId}`
      : findId
      ? `edit:${findId}`
      : 'create';
    const showForm = cloneFindId
      ? cloneCalled && Boolean(formFind)
      : !findId || (called && Boolean(formFind));

    if (
      findLoading ||
      sitesLoading ||
      groupsLoading ||
      availableFiltersLoading
    ) {
      return <FindSkeleton />;
    }

    return (
      <>
        {findCalled && !findLoading && !formFind && <FindNotFound />}
        {showForm && (
          <FindForm
            key={formKey}
            id={cloneFindId ? undefined : findId}
            find={formFind}
            eras={eras}
            materials={materials}
            types={types}
            sites={sites}
            site={forSite}
            tags={tags}
            groups={groups}
            initialGroupId={searchParams.get('group') || undefined}
            isClone={Boolean(cloneFindId)}
            onSaveDisabledChange={onSaveDisabledChange}
          />
        )}
      </>
    );
  }
);

export { FindSettings };
